import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';

const AUDIO_SRC = '/audio/humanity-ambient.mp3';
const STORAGE_KEY = 'humanity_sound_muted';

// Volume targets per artwork state. Smaller while the entity speaks so the
// soundtrack ducks under the voice without ever muting ElevenLabs itself.
const VOLUME_FOR_STATE = {
  speaking: 0.05,
  listening: 0.08,
  muted: 0.06,
  waking: 0.10,
  ended: 0.12,
  generating: 0.10,
  sleeping: 0.16,
  error: 0.16,
};
const DEFAULT_VOLUME = 0.16;
const FADE_DURATION_MS = 2200;

/**
 * AmbientSound — quiet background soundtrack for /whatishumanity.
 *
 * Lightweight, standalone (no Web Audio interaction system). One looping
 * <audio> element that fades in on first successful play, ducks under
 * ElevenLabs voice via `voiceState`, and remembers the user's mute
 * preference in localStorage. Mute toggle is a tiny circular glass button
 * bottom-left.
 */
export default function AmbientSound({ voiceState = 'sleeping' }) {
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const fadeRafRef = useRef(0);
  const targetVolRef = useRef(0);

  const fadeToVolume = (target, durationMs = FADE_DURATION_MS) => {
    const el = audioRef.current;
    if (!el) return;
    targetVolRef.current = target;
    const start = el.volume;
    const t0 = performance.now();
    if (fadeRafRef.current) cancelAnimationFrame(fadeRafRef.current);
    const step = (now) => {
      if (targetVolRef.current !== target) return;
      const k = Math.min(1, (now - t0) / Math.max(1, durationMs));
      const e = 1 - (1 - k) ** 3;
      el.volume = Math.max(0, Math.min(1, start + (target - start) * e));
      if (k < 1) fadeRafRef.current = requestAnimationFrame(step);
    };
    fadeRafRef.current = requestAnimationFrame(step);
  };

  // Browsers block audio autoplay until first user gesture. We try play()
  // optimistically; on rejection we wait for the first interaction and retry.
  const tryPlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (muted) return;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        const onGesture = () => {
          window.removeEventListener('pointerdown', onGesture);
          window.removeEventListener('touchstart', onGesture);
          window.removeEventListener('keydown', onGesture);
          const el2 = audioRef.current;
          if (!el2 || muted) return;
          const p2 = el2.play();
          if (p2 && typeof p2.catch === 'function') {
            p2.catch((err) => {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[HumanityAmbient] still blocked after gesture', err?.message);
              }
            });
          }
        };
        window.addEventListener('pointerdown', onGesture, { once: true, passive: true });
        window.addEventListener('touchstart', onGesture, { once: true, passive: true });
        window.addEventListener('keydown', onGesture, { once: true });
      });
    }
  };

  // ── initial mount: start at 0 vol, attempt play, fade in ─────
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    el.volume = 0;
    el.loop = true;
    if (!muted) {
      tryPlay();
      fadeToVolume(DEFAULT_VOLUME, FADE_DURATION_MS);
    }
    return () => { if (fadeRafRef.current) cancelAnimationFrame(fadeRafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── react to voiceState changes (ducks under ElevenLabs) ─────
  useEffect(() => {
    if (muted) {
      fadeToVolume(0, 900);
      return;
    }
    const target = VOLUME_FOR_STATE[voiceState] ?? DEFAULT_VOLUME;
    fadeToVolume(target, 1100);
  }, [voiceState, muted]);

  // ── react to mute toggle ─────────────────────────────────────
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
    const el = audioRef.current;
    if (!el) return;
    if (muted) {
      fadeToVolume(0, 700);
      setTimeout(() => {
        const el2 = audioRef.current;
        if (el2 && targetVolRef.current === 0) {
          try { el2.pause(); } catch { /* ignore */ }
        }
      }, 750);
    } else {
      tryPlay();
      const target = VOLUME_FOR_STATE[voiceState] ?? DEFAULT_VOLUME;
      fadeToVolume(target, FADE_DURATION_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  const Icon = muted ? VolumeX : Volume2;

  return (
    <>
      <audio
        ref={audioRef}
        src={AUDIO_SRC}
        loop
        preload="auto"
        playsInline
        // eslint-disable-next-line react/no-unknown-property
        x-webkit-airplay="deny"
        aria-hidden
        style={{ display: 'none' }}
        data-testid="humanity-ambient-audio"
      />
      <motion.button
        type="button"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5 }}
        onClick={() => setMuted((m) => !m)}
        title={muted ? 'Sound off' : 'Sound on'}
        aria-label={muted ? 'Unmute ambient sound' : 'Mute ambient sound'}
        data-testid="ambient-sound-toggle"
        data-clickable="true"
        className="fixed bottom-4 left-4 sm:bottom-5 sm:left-5 z-30 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${muted ? 'rgba(255,255,255,0.18)' : 'rgba(155,230,244,0.35)'}`,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: muted ? 'rgba(245,245,245,0.55)' : '#9BE6F4',
        }}
        whileHover={{
          scale: 1.06,
          borderColor: 'rgba(155,230,244,0.7)',
          color: '#9BE6F4',
        }}
        whileTap={{ scale: 0.94 }}
      >
        <Icon size={14} strokeWidth={1.6} />
      </motion.button>
    </>
  );
}
